import { useCallback, useEffect, useMemo, useState } from 'react';
import { ControlledAccordion } from './useControlledAccordions';

interface UseCollapsibleAccordionProps {
  defaultExpanded?: boolean;
  controlled?: ControlledAccordion;
}

/*
 * This hook supports:
 * 1. Uncontrolled expand toggling (standalone accordion)
 * 2. Controlled expand toggling (as part of a controlled list of accordions)
 */
export function useCollapsibleAccordion({
  defaultExpanded,
  controlled,
}: UseCollapsibleAccordionProps) {
  const [isExpanded, setExpanded] = useState(
    controlled ? controlled.isExpanded : (defaultExpanded ?? false),
  );

  const isControlled = typeof controlled !== 'undefined';

  const uncontrolledChange = useCallback(() => {
    setExpanded(expanded => !expanded);
  }, []);

  const controlledChange = useCallback(() => {
    if (typeof controlled !== 'undefined') {
      controlled.changeExpanded(controlled.id);
    }
  }, [controlled]);

  const changeLocalExpanded = useMemo(() => {
    return isControlled ? controlledChange : uncontrolledChange;
  }, [isControlled, controlledChange, uncontrolledChange]);

  // Trigger component setState to match the control parent
  useEffect(() => {
    if (typeof controlled?.isExpanded === 'boolean') {
      setExpanded(expanded =>
        expanded !== controlled.isExpanded ? controlled.isExpanded : expanded,
      );
    }
  }, [controlled?.isExpanded]);

  return { isExpanded, changeExpanded: changeLocalExpanded };
}

export default useCollapsibleAccordion;
